/**
 * shared/demoDataFilter.ts
 *
 * W10-DemoDataScrub — Centralized demo/smoke-test data filter utility.
 *
 * Tenant 1 (Mi6 AIP, Supabase gstkgiwdjvztopjbeoiy) has SMOKE_TEST_* and
 * [SIM-* rows in cadence_prospects, accounts, and related tables from QA
 * seeding. These must never appear on rep-facing surfaces.
 *
 * DO NOT DELETE the underlying rows — filter only at the query layer.
 *
 * Usage (server, SQL layer):
 *   import { excludeDemoDataSql } from "../../shared/demoDataFilter";
 *   query.filter(excludeDemoDataSql("name"));
 *   // — or inline in raw SQL:
 *   WHERE ${excludeDemoDataSql("company")}
 *
 * Usage (client, runtime predicate):
 *   import { isDemoData } from "../../shared/demoDataFilter";
 *   const clean = accounts.filter(a => !isDemoData(a.name));
 *
 * Admin escape hatch:
 *   Pass ?includeDemoData=true on any filtered endpoint to bypass.
 *   The server reads this param and skips the filter when present.
 *   Default is OFF (reps never see demo data).
 *
 * No exclamation points. No emojis.
 * Authors: W10-DemoDataScrub lane.
 */

// ── Canonical pattern list ─────────────────────────────────────────────────

/**
 * SQL LIKE patterns that identify demo / smoke-test account names.
 * Used in excludeDemoDataSql() and referenced in SQL WHERE clauses.
 */
export const DEMO_DATA_NAME_PATTERNS: readonly string[] = [
  "[SIM-%",
  "SMOKE_TEST_%",
  "%_test_%",
];

/**
 * SQL LIKE patterns that identify demo email addresses.
 */
export const DEMO_DATA_EMAIL_PATTERNS: readonly string[] = [
  "smoke_test%@%",
  "test+%@%",
];

// ── TypeScript / client-side predicate ────────────────────────────────────

/**
 * Returns true if the given string matches any known demo-data pattern.
 * Safe to call with null or undefined — returns false in those cases.
 *
 * Use this as a client-side guard to filter arrays of records before render:
 *   const safe = accounts.filter(a => !isDemoData(a.name));
 */
export function isDemoData(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.toLowerCase();
  return (
    n.startsWith("[sim-") ||
    n.startsWith("smoke_test_") ||
    // matches _test_ anywhere (with surrounding underscores)
    /_test_/.test(n)
  );
}

/**
 * Returns true if the given email matches any known demo-data email pattern.
 * Safe to call with null or undefined.
 */
export function isDemoEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const e = email.toLowerCase();
  return e.startsWith("smoke_test") || e.startsWith("test+");
}

// ── SQL clause builder ─────────────────────────────────────────────────────

/**
 * Returns an SQL AND clause (with leading AND) that excludes demo rows for a
 * given column name. Designed to be appended to an existing WHERE clause.
 *
 * Example:
 *   SELECT * FROM accounts WHERE tenant_id = $1
 *     ${excludeDemoDataSql("name")}
 *
 * Produces:
 *   AND (name NOT LIKE '[SIM-%' AND name NOT LIKE 'SMOKE_TEST_%' AND name NOT LIKE '%_test_%')
 *
 * @param columnName  The unquoted column identifier (e.g. "name", "company")
 * @returns           SQL fragment beginning with AND, safe to append inline
 */
export function excludeDemoDataSql(columnName: string): string {
  const col = columnName; // caller is responsible for safe column names
  const clauses = DEMO_DATA_NAME_PATTERNS.map(
    (pattern) => `${col} NOT LIKE '${pattern}'`
  ).join(" AND ");
  return `AND (${clauses})`;
}

/**
 * Returns an SQL AND clause that excludes demo email addresses for a given
 * column name.
 *
 * Example:
 *   SELECT * FROM contacts WHERE tenant_id = $1
 *     ${excludeDemoEmailSql("email")}
 */
export function excludeDemoEmailSql(columnName: string): string {
  const col = columnName;
  const clauses = DEMO_DATA_EMAIL_PATTERNS.map(
    (pattern) => `${col} NOT LIKE '${pattern}'`
  ).join(" AND ");
  return `AND (${clauses})`;
}

// ── Supabase query helpers ─────────────────────────────────────────────────

/**
 * Applies all name-based demo-data exclusion filters to a Supabase
 * PostgREST query builder.
 *
 * @param query       A Supabase select/from builder
 * @param columnName  The column to filter on (default: "name")
 * @returns           The same query builder with NOT LIKE filters chained
 *
 * Usage:
 *   let q = sb.from("accounts").select("*").eq("tenant_id", tenantId);
 *   q = applyDemoDataFilter(q, "name");
 *
 * Note: Supabase PostgREST uses `not.like` for NOT LIKE filters.
 */
export function applyDemoDataFilter<T>(
  query: T & {
    not: (column: string, op: string, value: string) => T;
  },
  columnName = "name"
): T {
  let q = query;
  for (const pattern of DEMO_DATA_NAME_PATTERNS) {
    q = q.not(columnName, "like", pattern) as T;
  }
  return q;
}

/**
 * Applies email-based demo-data exclusion filters to a Supabase query builder.
 *
 * @param query       A Supabase select/from builder
 * @param columnName  The email column to filter on (default: "email")
 * @returns           The same query builder with NOT LIKE filters chained
 */
export function applyDemoEmailFilter<T>(
  query: T & {
    not: (column: string, op: string, value: string) => T;
  },
  columnName = "email"
): T {
  let q = query;
  for (const pattern of DEMO_DATA_EMAIL_PATTERNS) {
    q = q.not(columnName, "like", pattern) as T;
  }
  return q;
}

// ── Admin escape hatch helper ──────────────────────────────────────────────

/**
 * Returns true when the request signals that demo data should be included.
 * Admins may append ?includeDemoData=true to any filtered endpoint.
 *
 * Usage in route handlers:
 *   if (!shouldIncludeDemoData(req.query)) {
 *     query = applyDemoDataFilter(query, "name");
 *   }
 */
export function shouldIncludeDemoData(
  query: Record<string, unknown> | NodeJS.Dict<string | string[]>
): boolean {
  const v = (query as Record<string, unknown>).includeDemoData;
  return v === "true" || v === "1" || v === true;
}
